import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Archive, SpinnerGap, CheckCircle, XCircle, StackSimple, Rocket, ShieldCheck,
  Copy, Trash, Sparkle, CloudArrowUp, Prohibit,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const REUSE_OPTIONS = [
  { v: "private", l: "Private (only me)" },
  { v: "family_only", l: "Family only" },
  { v: "specific_sites", l: "Specific SocialPulse sites" },
  { v: "all_socialpulse", l: "All SocialPulse properties" },
  { v: "public_display", l: "Public display" },
  { v: "public_attribution", l: "Public reuse with attribution" },
  { v: "commercial", l: "Commercial use permitted" },
  { v: "embed_only", l: "Original-link / embed only" },
];
const SITES = [
  { v: "socialpulse", l: "SocialPulse" }, { v: "indianfamilytree", l: "FamilyTree" },
  { v: "alumnipulse", l: "AlumniPulse" }, { v: "travelerpulse", l: "TravelerPulse" },
  { v: "foodpulse", l: "FoodPulse" }, { v: "naturepulse", l: "NaturePulse" },
];
const CHUNK = 5 * 1024 * 1024; // 5MB

const STATUS_STYLE = {
  queued: "bg-zinc-100 text-zinc-600",
  running: "bg-blue-100 text-[#F27A54]",
  done: "bg-emerald-100 text-emerald-700",
  failed: "bg-red-100 text-red-700",
  canceling: "bg-amber-100 text-amber-700",
  canceled: "bg-zinc-200 text-zinc-500",
};

const TYPE_LABEL = { zip: "Folder / Zip import", google: "Google Photos import", enrich: "AI enrich & publish" };

export default function BulkIngest() {
  const zipRef = useRef();
  const [reuse, setReuse] = useState("public_attribution");
  const [sites, setSites] = useState([]);
  const [publishOnEnrich, setPublishOnEnrich] = useState(true);

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const [jobs, setJobs] = useState([]);
  const [drafts, setDrafts] = useState(0);

  const toggleSite = (v) => setSites((s) => (s.includes(v) ? s.filter((x) => x !== v) : [...s, v]));
  const reuseSites = () => (reuse === "private" || reuse === "family_only" ? [] : sites);

  const refresh = useCallback(async () => {
    try {
      const [j, d] = await Promise.all([api.get("/ingest/jobs"), api.get("/ingest/drafts/count")]);
      setJobs(j.data);
      setDrafts(d.data.count);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 2500);
    return () => clearInterval(t);
  }, [refresh]);

  const uploadZip = async (file) => {
    if (!file) return;
    if (!/\.zip$/i.test(file.name)) { toast.error("Please choose a .zip archive (e.g. a Google Takeout or phone export)."); return; }
    setUploading(true); setProgress(0);
    try {
      const { data: init } = await api.post("/ingest/upload/init");
      const uploadId = init.upload_id;
      const total = Math.ceil(file.size / CHUNK);
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("index", i);
        fd.append("file", blob, "chunk");
        await api.post("/ingest/upload/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      await api.post("/ingest/zip", {
        upload_id: uploadId, filename: file.name,
        reuse_permission: reuse, usable_on_sites: reuseSites(),
      });
      toast.success("Archive uploaded — importing in the background. You can leave this page.");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); setProgress(0); if (zipRef.current) zipRef.current.value = ""; }
  };

  const enrichPublish = async () => {
    if (!drafts) { toast.info("No draft photos waiting to be enriched."); return; }
    if (reuse === "specific_sites" && !sites.length) { toast.error("Pick at least one site for 'Specific SocialPulse sites'."); return; }
    try {
      await api.post("/ingest/enrich", { reuse_permission: reuse, usable_on_sites: reuseSites(), publish: publishOnEnrich });
      toast.success(`Enriching ${drafts} draft photo(s) with AI${publishOnEnrich ? " and publishing" : ""} — running in the background.`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start enrichment"); }
  };

  const cancel = async (id) => { await api.post(`/ingest/jobs/${id}/cancel`); refresh(); };

  return (
    <div className="mx-auto max-w-5xl">
      <p className="overline text-[#F27A54]">Import · Bulk pipeline</p>
      <h1 className="mt-1 font-display text-3xl font-black tracking-tight">Bulk ingest &amp; publish</h1>
      <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
        Rescue your photos from the phone "cemetery." Import huge archives in the background, then enrich the ones you
        want with AI and publish them to the network — no need to keep this tab open.
      </p>

      {/* Shared batch rights */}
      <Card className="mt-6 rounded-md border border-border p-5 shadow-none" data-testid="ingest-rights-card">
        <div className="flex items-center gap-2.5">
          <ShieldCheck size={20} weight="fill" className="text-[#F27A54]" />
          <div>
            <h3 className="font-display text-sm font-bold">Rights &amp; reuse for imports</h3>
            <p className="text-xs text-muted-foreground">Applied to archives you upload and to photos you enrich &amp; publish below.</p>
          </div>
        </div>
        <div className="mt-4 border-t border-border pt-4">
          <label className="overline mb-1 block text-[10px] text-muted-foreground">Reuse permission</label>
          <Select value={reuse} onValueChange={setReuse}>
            <SelectTrigger data-testid="ingest-reuse" className="h-9 w-64 rounded-md"><SelectValue /></SelectTrigger>
            <SelectContent>{REUSE_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
          </Select>
          {reuse !== "private" && reuse !== "family_only" && (
            <div className="mt-3" data-testid="ingest-sites-group">
              <label className="overline mb-1.5 block text-[10px] text-muted-foreground">
                {reuse === "specific_sites" ? "Which sites can use these photos?" : "Prefer specific sites (optional)"}
              </label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {SITES.map((s) => {
                  const on = sites.includes(s.v);
                  return (
                    <button type="button" key={s.v} data-testid={`ingest-site-${s.v}`} onClick={() => toggleSite(s.v)}
                      className={`flex items-center gap-2 rounded-md border px-3 py-2 text-left text-xs font-semibold transition-colors ${
                        on ? "border-[#F27A54] bg-blue-50 text-[#F27A54]" : "border-border bg-white text-zinc-600 hover:border-[#F27A54]/50"
                      }`}>
                      <span className={`grid h-4 w-4 shrink-0 place-items-center rounded-sm border ${on ? "border-[#F27A54] bg-[#F27A54] text-white" : "border-zinc-300"}`}>
                        {on && <CheckCircle size={12} weight="fill" />}
                      </span>
                      {s.l}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </Card>

      <div className="mt-5 grid gap-5 md:grid-cols-2">
        {/* ZIP upload */}
        <Card className="rounded-md border border-border p-5 shadow-none" data-testid="ingest-zip-card">
          <div className="flex items-center gap-2.5">
            <Archive size={22} weight="fill" className="text-[#F27A54]" />
            <div>
              <h3 className="font-display text-sm font-bold">Upload a folder archive (.zip)</h3>
              <p className="text-xs text-muted-foreground">Export from your phone or a Google Takeout, zip it, drop it here. Uploaded in resumable chunks.</p>
            </div>
          </div>
          <div className="mt-4 border-t border-border pt-4">
            <input ref={zipRef} type="file" accept=".zip,application/zip" className="hidden" data-testid="ingest-zip-input"
              onChange={(e) => uploadZip(e.target.files?.[0])} />
            <Button data-testid="ingest-zip-btn" onClick={() => zipRef.current?.click()} disabled={uploading}
              className="h-10 w-full gap-2 rounded-md bg-[#F27A54] hover:bg-[#d9633f]">
              {uploading ? <SpinnerGap size={16} weight="bold" className="animate-spin" /> : <CloudArrowUp size={16} weight="bold" />}
              {uploading ? `Uploading… ${progress}%` : "Choose .zip archive"}
            </Button>
            {uploading && (
              <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-zinc-100">
                <div className="h-full rounded-full bg-[#F27A54] transition-all" style={{ width: `${progress}%` }} />
              </div>
            )}
            <p className="mt-2 text-xs text-muted-foreground">Photos land as private <strong>drafts</strong> (fast pass: GPS + dedupe, no AI yet).</p>
          </div>
        </Card>

        {/* Enrich & publish */}
        <Card className="rounded-md border border-border p-5 shadow-none" data-testid="ingest-enrich-card">
          <div className="flex items-center gap-2.5">
            <Rocket size={22} weight="fill" className="text-[#F27A54]" />
            <div>
              <h3 className="font-display text-sm font-bold">Enrich &amp; publish drafts</h3>
              <p className="text-xs text-muted-foreground">Run AI + location on your drafts, then publish to the network.</p>
            </div>
          </div>
          <div className="mt-4 border-t border-border pt-4">
            <div className="flex items-center gap-2 rounded-md bg-blue-50 px-3 py-2 text-sm font-semibold text-[#F27A54]" data-testid="ingest-drafts-count">
              <StackSimple size={16} weight="fill" /> {drafts.toLocaleString()} draft photo(s) waiting
            </div>
            <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm font-medium text-zinc-700" data-testid="ingest-publish-toggle">
              <input type="checkbox" checked={publishOnEnrich} onChange={(e) => setPublishOnEnrich(e.target.checked)}
                className="h-4 w-4 rounded border-zinc-300 text-[#F27A54] focus:ring-[#F27A54]" />
              Publish publicly after enriching (otherwise save as approved)
            </label>
            <Button data-testid="ingest-enrich-btn" onClick={enrichPublish} disabled={!drafts}
              className="mt-3 h-10 w-full gap-2 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50">
              <Sparkle size={16} weight="fill" /> Enrich {drafts ? drafts.toLocaleString() : ""} &amp; {publishOnEnrich ? "publish" : "approve"}
            </Button>
          </div>
        </Card>
      </div>

      {/* Jobs */}
      <div className="mt-8">
        <h2 className="font-display text-lg font-bold">Background jobs</h2>
        <p className="text-xs text-muted-foreground">Live progress — safe to close this tab, jobs keep running.</p>
        <div className="mt-3 space-y-3" data-testid="ingest-jobs-list">
          {jobs.length === 0 && <p className="rounded-md border border-dashed border-border py-8 text-center text-sm text-muted-foreground">No jobs yet. Upload an archive or enrich your drafts to get started.</p>}
          {jobs.map((j) => {
            const total = j.total || 0;
            const done = (j.imported || 0) + (j.duplicates || 0) + (j.failed || 0);
            const pct = total ? Math.round((done / total) * 100) : (j.status === "done" ? 100 : 0);
            const active = j.status === "running" || j.status === "queued" || j.status === "canceling";
            return (
              <Card key={j.id} data-testid={`ingest-job-${j.id}`} className="rounded-md border border-border p-4 shadow-none">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="font-display text-sm font-bold">{TYPE_LABEL[j.type] || j.type}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_STYLE[j.status] || "bg-zinc-100 text-zinc-600"}`}>{j.status}</span>
                  </div>
                  {active && (
                    <button data-testid={`ingest-cancel-${j.id}`} onClick={() => cancel(j.id)}
                      className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-semibold text-zinc-600 hover:bg-zinc-100">
                      <Prohibit size={13} weight="bold" /> Cancel
                    </button>
                  )}
                </div>
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-zinc-100">
                  <div className={`h-full rounded-full transition-all ${j.status === "failed" ? "bg-red-500" : j.status === "canceled" ? "bg-zinc-400" : "bg-[#F27A54]"}`} style={{ width: `${pct}%` }} />
                </div>
                <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
                  <span>{done.toLocaleString()} / {total.toLocaleString()} processed</span>
                  <span className="flex items-center gap-1 text-emerald-600"><CheckCircle size={12} weight="fill" />{(j.imported || 0).toLocaleString()} imported</span>
                  {j.type !== "enrich" && <span className="flex items-center gap-1 text-amber-600"><Copy size={12} weight="fill" />{(j.duplicates || 0).toLocaleString()} duplicates</span>}
                  {j.failed > 0 && <span className="flex items-center gap-1 text-red-600"><XCircle size={12} weight="fill" />{j.failed.toLocaleString()} failed</span>}
                </div>
                {j.message && <p className="mt-1 text-xs text-red-600">{j.message}</p>}
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
